import { useContext, useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { ThemeProvider, useTheme } from "styled-components";
import { css } from "@stubhub/broadway-foundation/css";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Filters } from "@viagogo/core/ui";
import {
  AddFeature,
  Feature,
  RecordParticipationTypes,
  useFeatureParticipationRecorder,
  useSimpleFeatureTernary,
} from "@viagogo/feature";
import { useContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { LocationContext } from "src/shared/context/location-context";
import { useIsStubhubUSEnglish } from "src/shared/utils/appContextHelper";
import { StyledClearHeroBanner } from "src/website-apps/responsive/stubhub-home/components/clear-hero-banner/styled-components";
import { HomeAiGreeting } from "src/website-apps/responsive/stubhub-home/components/HomeAiGreeting";
import { HomeEmphasizedSearch } from "src/website-apps/responsive/stubhub-home/components/HomeHeaderLayout/HomeEmphasizedSearch";
import { SearchEmphasizedBrowseHeader } from "src/website-apps/responsive/stubhub-home/components/SearchEmphasizedBrowseHeader";
import { HomePromptRecommendations } from "src/website-apps/shared/components/chat-prompt-recommendations";
import { DateSelector } from "src/website-apps/shared/components/date-selector";
import { MarketplaceDisclosure } from "src/website-apps/shared/components/disclosures";
import { FavoritesContext } from "src/website-apps/shared/components/grid/favorites-fs/FavoritesContextTyped";
import { BrowseHeader } from "src/website-apps/shared/components/header/BrowseHeader";
import { EnsembleAppNavHeader } from "src/website-apps/shared/components/header/ensemble-app-nav/EnsembleAppNavHeader";
import { MegaSearch } from "src/website-apps/shared/components/instant-search-redesign-mega-search/MegaSearch";
import { MegaSearchProvider } from "src/website-apps/shared/components/instant-search-redesign-mega-search/MegaSearchProvider";
import { LocationSelector } from "src/website-apps/shared/components/location-selector";
import { RelistBanner } from "src/website-apps/shared/components/sell-one-click-relist-banner";
import { DateContext } from "src/website-apps/shared/context/date-context";

const headerWrapper = css({
  desktop: {
    zIndex: 2,
  },
});

const filterChipWrapper = css({
  height: "full",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

// Always mounted on mobile — visibility toggled via transform to avoid
// IntersectionObserver-triggered unmounts while the search dialog is open.
//
// Padding aligns the search input with its position in the open dialog:
//   top:    0 + Trigger 4px + SHSearchInput margin-top 12px = 16px (= dialog spacing(4))
//           (MobileSearchInputTriggerTarget doesn't override SHSearchInput margin-top,
//            unlike StyledInput in the dialog which sets margin-top:0)
//   left:   40px
//   right:  16px, bottom: 8px
// Dynamic properties (transform, pointerEvents) are applied via inline style.
const compactSearchBar = css({
  position: "fixed",
  insetBlockStart: "primitives.0",
  insetInlineStart: "primitives.0",
  insetInlineEnd: "primitives.0",
  zIndex: 10,
  backgroundColor: "bg.surface",
  borderBlockEndWidth: "[1px]",
  borderBlockEndStyle: "solid",
  borderBlockEndColor: "border",
  paddingBlockStart: "primitives.0",
  paddingInlineEnd: "primitives.200",
  paddingBlockEnd: "primitives.100",
  paddingInlineStart: "primitives.500",
  transitionProperty: "transform",
  transitionDuration: "[200ms]",
  transitionTimingFunction: "[ease-out]",
  willChange: "[transform]",
  tablet: {
    display: "none",
  },
});

/**
 * Home Header Layout
 *
 * The hero banner is always visible — when a filter is applied, the page scrolls
 * past it rather than hiding it (experiment CXWD_HomeFilterFocusMode).
 *
 * CXWD-3344: compact fixed search bar with 200ms slide-down animation
 * on mobile when the main header scrolls off screen.
 *
 * The bar stays mounted at all times (hidden via transform) to prevent
 * IntersectionObserver-driven unmounts from destroying the search dialog mid-open.
 */
export const HomeHeaderLayout = ({ mobileMenuOpen, model, onMobileMenuToggle }) => {
  const appContext = useAppContext();
  const isStubhubUSEnglish = useIsStubhubUSEnglish();
  const favorites = useContext(FavoritesContext);
  const { header, leagueId, showSthRelistMerchBanner } = model;
  const isTablet = useMediaQuery(Breakpoint.Tablet);
  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const [dates, setDates] = useContext(DateContext);
  const [userLocation, setUserLocation] = useContext(LocationContext);
  const locationSelectorPlaceholderContent = useContent("cid$#1904153#");
  const aiStyled =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMSearchAiCommerceChat,
      false,
      true,
      () => isStubhubUSEnglish && appContext.isUserLoggedIn,
    ) ?? false;
  const theme = useTheme();
  // TMSearchAiCommerceChat: force @viagogo/core's Header to use its phone layout (BurgerMenuHeader)
  // across the 600px–768px gap where SC tablet starts but Broadway tablet hasn't yet. Header reads
  // theme.media.phone via useTheme() for its JS layout switch, so extending it to 767px covers the gap.
  const mobileOnlyTheme = useMemo(
    () => ({
      ...theme,
      media: {
        ...theme.media,
        phone: "only screen and (min-width: 0px) and (max-width: 767px)",
        tablet: "not all",
        desktop: "not all",
      },
    }),
    [theme],
  );
  const { ref: sentinelRef, inView: sentinelInView } = useInView({
    threshold: 0,
    initialInView: true,
    rootMargin: "-1px 0px 0px 0px",
  });
  const isMobile = !isTablet && !isDesktop;

  const recordScrollParticipation = useFeatureParticipationRecorder(
    FeatureSetEnum.MAnchorSearchBarOnScroll,
    () => isMobile,
  );

  // Prevent browser from restoring scroll position on reload. Without this,
  // the scroll-save/restore in MobileSearchDialog causes the browser to
  // remember a non-zero scroll position and restore it after page reload.
  useEffect(() => {
    if (!("scrollRestoration" in history)) return;
    const prev = history.scrollRestoration;
    history.scrollRestoration = "manual";
    return () => {
      history.scrollRestoration = prev;
    };
  }, []);

  // Record experiment participation when user scrolls past the header section
  // (pristine gate: only users who scroll past are exposed to the variant)
  useEffect(() => {
    if (!sentinelInView) {
      recordScrollParticipation();
    }
  }, [sentinelInView, recordScrollParticipation]);

  const renderFilters = () => (
    <Filters
      leftFilter={() => (
        <div className={filterChipWrapper}>
          <LocationSelector
            location={userLocation}
            onLocationChange={setUserLocation}
            placeholder={locationSelectorPlaceholderContent}
            placement="Header"
            chipVariant="ghost"
          />
        </div>
      )}
      rightFilter={() => (
        <div className={filterChipWrapper}>
          <DateSelector dates={dates ?? undefined} onDatesChange={setDates} chipVariant="ghost" />
        </div>
      )}
    />
  );

  return (
    <MegaSearchProvider>
      {/* Always mounted on mobile; transform drives show/hide with animation */}
      {isMobile && (
        <AddFeature
          id={FeatureSetEnum.MAnchorSearchBarOnScroll}
          recordType={RecordParticipationTypes.MANUAL}
          feature={() => (
            <div
              className={compactSearchBar}
              style={{
                transform: sentinelInView ? "translateY(-100%)" : "translateY(0)",
                pointerEvents: sentinelInView ? "none" : "auto",
              }}
            >
              <MegaSearch />
            </div>
          )}
        />
      )}
      <StyledClearHeroBanner $noBottomPadding>
        {showSthRelistMerchBanner && <RelistBanner leagueId={leagueId} />}
        {!appContext.isNativeApp && header.marketplaceDisclosure && (
          <MarketplaceDisclosure
            marketplaceDisclosureSiteMessage={header.marketplaceDisclosure}
            transparentBackground={false}
          />
        )}
        <div className={headerWrapper}>
          <Feature
            id={FeatureSetEnum.TMEnsembleAppNavHeaderDiscovery}
            control={() =>
              isTablet || isDesktop ? (
                <SearchEmphasizedBrowseHeader
                  bigPerformerBanner={false}
                  discoveryLinks={model.header.navigationBarLinks}
                  isUserLoggedIn={appContext.isUserLoggedIn}
                  greeting={aiStyled ? <HomeAiGreeting /> : undefined}
                />
              ) : (
                <ThemeProvider theme={aiStyled ? mobileOnlyTheme : theme}>
                  <BrowseHeader
                    bigPerformerBanner={false}
                    disclaimerSameBackgroundAsHeader
                    discoveryLinks={model.header.navigationBarLinks}
                    favorites={favorites}
                    isUserLoggedIn={appContext.isUserLoggedIn}
                    mobileMenuOpen={mobileMenuOpen}
                    onMobileMenuToggle={onMobileMenuToggle}
                    showSearch
                    greeting={aiStyled ? <HomeAiGreeting /> : undefined}
                    belowSearch={aiStyled ? <HomePromptRecommendations limit={4} /> : undefined}
                    filters={renderFilters}
                  />
                </ThemeProvider>
              )
            }
            variant={() => (
              <>
                <EnsembleAppNavHeader isUserLoggedIn={appContext.isUserLoggedIn} hideSearch />
                {/* Tablet/desktop prompt recommendations come from the AddFeature block below; only mobile passes belowSearch. */}
                <HomeEmphasizedSearch
                  greeting={aiStyled ? <HomeAiGreeting /> : undefined}
                  belowSearch={isMobile && aiStyled ? <HomePromptRecommendations limit={4} /> : undefined}
                />
                {isMobile && renderFilters()}
              </>
            )}
          />
        </div>
        {(isTablet || isDesktop) && (
          <AddFeature
            id={FeatureSetEnum.TMSearchAiCommerceChat}
            canParticipate={() => isStubhubUSEnglish && appContext.isUserLoggedIn}
            feature={() => <HomePromptRecommendations limit={4} />}
          />
        )}
        {/* Sentinel: when this leaves the viewport, the header (search bar) is out of view */}
        <div ref={sentinelRef} />
      </StyledClearHeroBanner>
    </MegaSearchProvider>
  );
};
